import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, Button, FlexContainer } from '../styles/StyledComponents';
import { useGameContext } from '../context/GameContext';

const DashboardContainer = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 2rem;
  margin: 2rem 0;
`;

const ProgressTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background-color: #f9f9f9;
    font-weight: bold;
  }

  tr:hover {
    background-color: #f9f9f9;
  }
`;

const LoginForm = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
  }

  input {
    width: 100%;
    padding: 0.75rem;
    margin-bottom: 1.5rem;
    border: 1px solid #ccc;
    border-radius: var(--border-radius);
    font-size: 1rem;
    font-family: var(--font-family);
  }
`;

const AccessCode = 'parent123'; // Simple access code for demo purposes

const ParentDashboard: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [accessCode, setAccessCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { games, progress } = useGameContext();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();

    if (accessCode === AccessCode) {
      setIsAuthenticated(true);
      setError(null);
    } else {
      setError('Invalid access code. Please try again.');
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  if (!isAuthenticated) {
    return (
      <Container>
        <PageTitle>Parent Dashboard</PageTitle>
        <LoginForm onSubmit={handleLogin}>
          <h2>Parent Access</h2>
          <p>Enter the parent access code to view your child's progress.</p>

          {error && <p style={{ color: 'var(--error-color)' }}>{error}</p>}

          <label htmlFor="accessCode">Access Code:</label>
          <input
            type="password"
            id="accessCode"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            required
          />

          <Button type="submit">Access Dashboard</Button>

          <p style={{ marginTop: '1rem', textAlign: 'center' }}>
            <Link to="/" style={{ color: 'var(--primary-color)' }}>
              Back to Games
            </Link>
          </p>
        </LoginForm>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>Parent Dashboard</PageTitle>

      <DashboardContainer>
        <h2>Your Child's Progress</h2>

        <ProgressTable>
          <thead>
            <tr>
              <th>Game</th>
              <th>Score</th>
              <th>Last Played</th>
            </tr>
          </thead>
          <tbody>
            {games.map(game => {
              const gameProgress = progress.find(p => p.gameId === game.id);

              return (
                <tr key={game.id}>
                  <td>{game.name}</td>
                  <td>{gameProgress ? gameProgress.score : 0}</td>
                  <td>
                    {gameProgress ? formatDate(gameProgress.lastPlayed) : 'Never played'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ProgressTable>

        <h3>Game Insights</h3>
        <p>This section will provide insights about your child's learning progress. Future updates will include:</p>
        <ul>
          <li>Learning strengths and areas for improvement</li>
          <li>Recommended activities</li>
          <li>Customizable game settings</li>
          <li>Detailed analytics</li>
        </ul>

        <FlexContainer justify="space-between" style={{ marginTop: '2rem' }}>
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <Button onClick={() => setIsAuthenticated(false)}>Log Out</Button>
        </FlexContainer>
      </DashboardContainer>
    </Container>
  );
};

export default ParentDashboard;
